import { Stack, Typography } from '@mui/joy';
import { omit } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Coin from 'assets/coin.svg';
import CurrencyCircleDollar from 'assets/currencyCircleDollar.svg';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { Image, LabeledValue, Tooltip } from 'core/elements';
import { formatDate } from 'core/utils';
import { PricingSourceType } from 'pricing/enums';
import { CostSourceHelper, PricingHelper } from 'pricing/helpers';
import { useCostDecimals } from 'pricing/hooks';
import { QuoteEntityType } from 'quotes/enums';
import { QuotePartHelper } from 'quotes/helpers';
import { ICustomerQuotePart, IPricingSourceData } from 'quotes/types';
import {
  getCostInfoValidityDates,
  getSelectedSpaIdentity,
  isOneTimeSpaExpirationDateMissing,
  isSelectedSpaSource,
} from 'quotes/utils';
import { Color, Level, TooltipPosition } from 'theme/enums';

import { ExternalPricingSourceContent } from './ExternalPricingSourceContent';

export const CostInfoTooltip = ({
  quotePart,
  quoteEntityType = QuoteEntityType.QUOTE,
  pricingData,
  spaCostCurrency,
}: {
  // From getEffectiveSpaInfo.
  pricingData: IPricingSourceData;
  quoteEntityType: QuoteEntityType;
  quotePart: ICustomerQuotePart;
  spaCostCurrency?: string | null;
}) => {
  const { t } = useTranslation();
  const costDecimals = useCostDecimals();

  const isAlternateVersionEnabled = Boolean(quotePart.alternateVersionEnabled);
  const isShipAndDebitVendor = quotePart.supplier?.isShipAndDebit;

  const isVendorQuoteNumberValid = QuotePartHelper.isVendorQuoteNumberValid(quotePart);
  const isReplacementCostInfoValid = QuotePartHelper.isReplacementCostInfoValid(
    quotePart,
    quoteEntityType
  );
  const isValidUntilMissing = isOneTimeSpaExpirationDateMissing(quotePart);

  // A selected SPA is the cost in use, so its section shows whatever the vendor.
  const isSelectedSpaLine = isSelectedSpaSource(pricingData?.sourceType);
  const hasSpaData = Object.values(omit(pricingData, 'sourceType')).some((value) => value);

  const displaySpaInfo =
    isAlternateVersionEnabled && hasSpaData && (isShipAndDebitVendor || isSelectedSpaLine);

  const costInfo = useMemo(() => {
    const {
      costSourceQuoteVendorName,
      costSourceQuoteRevisionNumber,
      replacementCost,
      replacementCostNumber,
    } = quotePart;

    const validityDates = getCostInfoValidityDates(quotePart);

    let info: Record<string, number | string> = {};

    if (PricingSourceType.EXTERNAL !== quotePart.costSourceType) {
      // The SPA block below already names the agreement's code and Reg Number.
      info = getSelectedSpaIdentity(quotePart)
        ? { labelCostSource: CostSourceHelper.getTypeLabel(quotePart).long }
        : {
            labelCostSource: CostSourceHelper.getTypeLabel(quotePart).long,
            labelVendor: costSourceQuoteVendorName,
            labelVendorQuoteNumber: QuotePartHelper.getVendorQuoteNumber(quotePart),
            labelQuoteVersion: costSourceQuoteRevisionNumber,
            ...(validityDates && {
              labelValidSince: formatDate(validityDates.validSince),
              labelValidUntil: formatDate(validityDates.validUntil),
            }),
          };
    }

    if (isShipAndDebitVendor) {
      info = {
        ...info,
        labelReplacementCost: PricingHelper.formatPricing({
          amount: replacementCost,
          decimals: costDecimals,
          fixedDecimals: true,
        }),
        labelReplacementCostNumber: replacementCostNumber,
      };
    }

    return info;
  }, [isShipAndDebitVendor, quotePart, costDecimals]);

  if (!isAlternateVersionEnabled || PricingSourceType.EXTERNAL === quotePart.costSourceType) {
    return <></>;
  }

  if (!isVendorQuoteNumberValid || !isReplacementCostInfoValid || isValidUntilMissing) {
    return (
      <Tooltip
        title={
          <Stack sx={{ gap: 0 }}>
            {!isVendorQuoteNumberValid && (
              <Typography level={Level.BODY_SM} sx={{ whiteSpace: 'nowrap' }}>
                {t('messageVendorQuoteNumberRequired')}
              </Typography>
            )}
            {!isReplacementCostInfoValid && (
              <Typography level={Level.BODY_SM} sx={{ whiteSpace: 'nowrap' }}>
                {t('messageReplacementCostInfoRequired')}
              </Typography>
            )}
            {isValidUntilMissing && (
              <Typography level={Level.BODY_SM} sx={{ whiteSpace: 'nowrap' }}>
                {t('messageCostValidUntilMissing')}
              </Typography>
            )}
          </Stack>
        }
        color={Color.WARNING}
        placement={TooltipPosition.TOP}
        childrenSx={{ padding: 0 }}
      >
        <Image src={CurrencyCircleDollar} />
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={
        <Stack sx={{ gap: 0, borderRadius: '8px', overflow: 'hidden' }}>
          <Stack
            sx={{
              gap: 0,
              padding: '6px',
            }}
          >
            {Object.keys(costInfo).map((key) => (
              <LabeledValue
                key={key}
                label={t(key)}
                value={costInfo[key] || DEFAULT_PLACEHOLDER}
                options={{
                  labelLevel: Level.BODY_XS,
                  labelSx: { color: 'inherit', whiteSpace: 'nowrap' },
                  inline: true,
                }}
              />
            ))}
          </Stack>

          {displaySpaInfo && (
            // Past the early return the cost is not external: only a pick is the cost in use.
            <Stack
              sx={{
                gap: 0,
                backgroundColor: 'neutral.300',
                padding: '6px',
                borderRadius: '8px',
              }}
            >
              <ExternalPricingSourceContent
                pricingData={pricingData}
                spaCostCurrency={spaCostCurrency}
                isStruckThrough={!isSelectedSpaLine}
              />
            </Stack>
          )}
        </Stack>
      }
      color={Color.WARNING}
      placement={TooltipPosition.TOP}
      sx={{ maxWidth: '400px', padding: '5px' }}
    >
      <Image src={Coin} />
    </Tooltip>
  );
};
